import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtDate, fmtErr } from "@/lib/api";

export default function AdminSupport() {
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("");

  const load = (f = filter) => api.get(`/admin/support${f ? `?status=${f}` : ""}`).then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(""); }, []);

  const setStatus = async (t, status) => {
    try {
      await api.post(`/admin/support/${t.id}/status`, { status });
      toast.success(`Ticket marked ${status}`);
      window.dispatchEvent(new Event("support-updated"));
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-support-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Support Requests</h1>
      <p className="mt-1 text-sm text-slate-600">Messages submitted by users via the Support page.</p>

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="admin-support-filters">
        {[["", "All"], ["open", "Open"], ["resolved", "Resolved"]].map(([v, label]) => (
          <button key={v} data-testid={`support-filter-${v || "all"}`} onClick={() => { setFilter(v); setTickets(null); load(v); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${filter === v ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {!tickets ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-support-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ticket</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Subject</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Message</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => (
                <tr key={t.id} data-testid={`support-row-${t.ticket_code}`}>
                  <td className="px-4 py-3.5 font-medium text-slate-900">{t.ticket_code}</td>
                  <td className="px-4 py-3.5">
                    <p className="font-medium text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.email}</p>
                  </td>
                  <td className="px-4 py-3.5 text-slate-800">{t.subject}</td>
                  <td className="px-4 py-3.5 text-slate-600 max-w-[240px]"><p className="line-clamp-2" title={t.message}>{t.message}</p></td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3.5"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3.5 text-right">
                    {t.status === "open" ? (
                      <button onClick={() => setStatus(t, "resolved")} data-testid={`resolve-ticket-${t.ticket_code}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                      </button>
                    ) : (
                      <button onClick={() => setStatus(t, "open")} data-testid={`reopen-ticket-${t.ticket_code}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">
                        <RotateCcw className="w-3.5 h-3.5" /> Reopen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tickets.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="support-empty">No support requests found.</p>}
        </div>
      )}
    </div>
  );
}
